import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { Avatar, Checkbox, Dropdown, Tooltip } from "antd";
import { MenuProps } from "antd/lib";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LearningMaterialV2Type } from "../../../../../types/learning-material";
import { getFirstLetter } from "../../../../../utils/functionUltils";
import { formatDateToIsoStringHHmm } from "../../../../../utils/converDate";
import { routesConfig } from "../../../../../config/routes";
import { sharePermissionType } from "../../../../../utils/sharePermission";
import "./ResourceCard.scss";

interface ResourceCardProps {
  tab: string;
  record: any;
  selected?: boolean;
  selectedItem?: any;
  selectedBank?: any;
  selectable?: boolean;
  selectedMode?: boolean;
  disableActions?: boolean;
  onSelect?: () => void;
  setSelectedItem?: (value: any) => void;
  onViewHistory?: (id: number) => void;
  onDelete?: (id: number, name: string) => void;
  onEdit?: (record: LearningMaterialV2Type) => void;
}

const ResourceCard: FC<ResourceCardProps> = ({
  tab,
  record,
  selected,
  selectedBank,
  selectedMode,
  selectedItem,
  disableActions,
  onEdit,
  onDelete,
  setSelectedItem,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getMenuItems = (rec: any): MenuProps["items"] => [
    {
      key: "3",
      label: (
        <div
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/${routesConfig.courseLibrary}/${routesConfig.sharePermission}/${rec.id}?type=${sharePermissionType.QUESTION_BANK}`
            );
          }}
        >
          <ShareAltOutlined />
          {t("&nbsp; Chia sẻ", "&nbsp; Chia sẻ")}
        </div>
      ),
    },
    {
      key: "1",
      label: t("Chỉnh sửa", "Chỉnh sửa"),
      icon: <EditOutlined />,
      onClick: (info) => {
        info.domEvent.stopPropagation();
        onEdit?.(record);
      },
    },
    {
      key: "2",
      label: t("Xóa", "Xóa"),
      icon: <DeleteOutlined />,
      className: "text-danger-del",
      onClick: (info) => {
        info.domEvent.stopPropagation();
        onDelete?.(record.id, record.name);
      },
    },
  ];

  return (
    <div
      className={`resource-card ${selected ? "selected" : ""}`}
      onClick={() =>
        selectedMode
          ? {}
          : navigate(
              `${record?.id}?name=${encodeURIComponent(
                record.name
              )}&permission=${
                tab === "2" && record.accessPermission === "2" ? "view" : "edit"
              }`
            )
      }
    >
      <div className="card-hover-overlay" />
      <div className="resource-card__header">
        <div className="flex align-center flex-column">
          <Tooltip placement="top" title={record.name}>
            <div className="learning-material-name">{record.name}</div>
          </Tooltip>
        </div>
        <div className="card-menu">
          {selectedMode ? (
            <Checkbox
              checked={selectedBank?.[0]?.id === record.id}
              onChange={() => {
                if (selectedItem?.id === record.id) {
                  setSelectedItem && setSelectedItem(null);
                } else {
                  setSelectedItem && setSelectedItem(record);
                }
              }}
            ></Checkbox>
          ) : !disableActions ? (
            <Dropdown
              menu={{ items: getMenuItems(record) }}
              trigger={["hover"]}
            >
              <a
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <MoreOutlined />
              </a>
            </Dropdown>
          ) : (
            <EyeOutlined style={{ color: "#757575" }} />
          )}
        </div>
      </div>
      <div className="resource-card__header-description">
        <div className="card-description">
          {record.description ? (
            <Tooltip
              overlayStyle={{ maxWidth: 300 }}
              placement="topLeft"
              title={record.description}
            >
              <div className="learning-material-description">
                {record.description}
              </div>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className="card-content">
        <>
          <div className="card-content__title">{t("Chủ sở hữu", "Chủ sở hữu")}</div>
          <div className="flex gap-16 align-center">
            <Avatar
              className="card-content__avatar"
              src={
                tab === "1"
                  ? record.authors?.[0]?.imagePath
                  : record.authors?.[0]?.avatar
              }
              size={33}
            >
              {getFirstLetter(
                tab === "1"
                  ? record.authors?.[0]?.userName
                  : record.authors?.[0]?.universityMember
              )}
            </Avatar>
            {tab === "1" ? (
              <div style={{ color: "#676767" }}>{t("tôi", "tôi")}</div>
            ) : (
              <div>
                <div style={{ color: "#676767" }} className="font-weight-5">
                  {record.authors?.[0]?.universityMember ||
                    record.authors?.[0]?.university}
                </div>
                <div
                  style={{
                    color: "#676767",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {record.authors?.[0]?.userName}
                </div>
              </div>
            )}
          </div>
        </>

        <div className="card-content__title">{t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối")}</div>
        <div className="flex gap-16 align-center">
          {tab === "1" ? (
            <div style={{ color: "#676767" }}>
              {formatDateToIsoStringHHmm(record.updatedDate)}
            </div>
          ) : (
            <>
              <Avatar
                src={
                  record.updateUser
                    ? record.updateUser?.universityLogo
                    : record.authors?.[0]?.avatar
                }
                className="card-content__avatar"
                size={33}
              >
                {getFirstLetter(
                  record.updateUser
                    ? record.updateUser?.productionUnitName
                    : record.authors?.[0]?.universityMember
                )}
              </Avatar>
              <div>
                <div style={{ color: "#676767" }} className="font-weight-5">
                  {record.updateUser
                    ? record.updateUser.universityName
                    : record.authors?.[0]?.universityMember}
                </div>
                <div
                  style={{
                    color: "#676767",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {record.updateUser
                    ? record.updateUser.name
                    : record.authors?.[0]?.userName}
                </div>
              </div>
              <div style={{ color: "#676767" }}>
                {formatDateToIsoStringHHmm(record.updatedDate)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
